/** API client — /api への JSON リクエストの薄いラッパ */

export interface ApiResponse<T = unknown> {
  success: boolean
  data?: T
  error?: string
}

async function request<T>(method: string, path: string, body?: unknown): Promise<ApiResponse<T>> {
  const res = await fetch(`/api${path}`, {
    method,
    credentials: 'same-origin',
    headers: body instanceof FormData ? undefined : { 'Content-Type': 'application/json' },
    body: body instanceof FormData ? body : body !== undefined ? JSON.stringify(body) : undefined,
  })
  // セッション失効はハードリダイレクトで復帰させる（short の handleResponse と同型）。
  // これが無いと失効後は全クエリが無言で失敗し続け、画面が固まって見える。
  // ハードリロードなので React Query 等の全状態もクリーンに捨てられる。
  // /auth/me（AuthContext の初期チェック）と /auth/login 自身は対象外 — Protected の
  // ルーティングに任せる（ここで飛ばすとログイン画面表示前に無限リダイレクトし得る）
  if (res.status === 401 && !path.startsWith('/auth/') && window.location.pathname !== '/login') {
    window.location.href = '/login'
    return { success: false, error: 'セッションの有効期限が切れました。ログインし直してください' }
  }
  const json = (await res.json().catch(() => ({}))) as ApiResponse<T>
  if (!res.ok && json.success === undefined) return { success: false, error: `HTTP ${res.status}` }
  return json
}

export const apiClient = {
  get: <T>(path: string) => request<T>('GET', path),
  post: <T>(path: string, body?: unknown) => request<T>('POST', path, body),
  put: <T>(path: string, body?: unknown) => request<T>('PUT', path, body),
  patch: <T>(path: string, body?: unknown) => request<T>('PATCH', path, body),
  delete: <T>(path: string) => request<T>('DELETE', path),
}

/**
 * API の失敗を表すエラー。サーバーが失敗時にも返す `data`（lint の不合格理由など）を保持する。
 * 従来の unwrap は Error(message) だけを投げて data を捨てていたため、
 * 「lint 不合格」の具体的な理由を画面に出せなかった（audit C11）
 */
export class ApiError<D = unknown> extends Error {
  readonly data?: D
  constructor(message: string, data?: D) {
    super(message)
    this.name = 'ApiError'
    this.data = data
  }
}

export function unwrap<T>(res: ApiResponse<T>): T {
  if (!res.success) throw new ApiError(res.error || 'API error', res.data)
  return res.data as T
}

/** 例外から画面に出すメッセージを取り出す（mutation の onError 共通） */
export function errorMessage(e: unknown, fallback = 'エラーが発生しました'): string {
  if (e instanceof Error && e.message) return e.message
  if (typeof e === 'string' && e) return e
  return fallback
}

export interface LintIssue {
  code: string
  level: string
  message: string
}

/** 編集保存の 400（lint 不合格）に付く data.lint.issues を取り出す。無ければ空配列 */
export function lintIssuesOf(e: unknown): LintIssue[] {
  if (!(e instanceof ApiError)) return []
  const lint = (e.data as { lint?: { issues?: unknown } } | undefined)?.lint
  if (!lint || !Array.isArray(lint.issues)) return []
  return lint.issues.filter(
    (i): i is LintIssue => !!i && typeof i === 'object' && typeof (i as LintIssue).message === 'string',
  )
}
